/**
 * @author        tae@zumper.com (Tae Yoon)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   location utilities
 */

import type { AutocompleteResult } from '@zcode/bizos'
import {
  DEFAULT_FALLBACK_BOX_DISTANCE,
  HOOD_FALLBACK_BOX_DISTANCE,
  STREET_FALLBACK_BOX_DISTANCE,
  TILE_SIZE,
  ZOOM_ADDRESS,
  ZOOM_CITY,
  ZOOM_CITY_MIN,
  ZOOM_HOOD,
  ZOOM_STATE,
  ZOOM_STREET,
  getFittedBounds,
  getFittedZoom,
  getQueryBounds,
  isValidBounds,
} from '@zcode/nr-utils'
import type { AutocompleteBoxType, Geo, SaveSearchLocationBox } from '@zcode/state-type'
import { isNumber } from '@zcode/utils'
import {
  AUTOCOMPLETE_ADDRESS,
  AUTOCOMPLETE_CITY,
  AUTOCOMPLETE_NEIGHBORHOOD,
  AUTOCOMPLETE_POSTCODE,
} from '../../../shared/components/Autocomplete/constants'
import * as LocationC from '../../../shared/constants/location'
import { calculateBoxForListing } from '../../../shared/utils'
import getBestHood from './getBestHood'
import getBoxFromHoodOrCity from './getBoxFromHoodOrCity'
import getClosestEntity from './getClosestEntity'
import getMatchingEntity from './getMatchingEntity'

// TODO(TAE): move these constants to a const file once we figure out how to do file structure
export const STREET_ADDRESS = 'street_address'
export const NEIGHBORHOOD = 'neighborhood'
export const LOCALITY = 'locality'
export const ROUTE = 'route'

function setZoom<T extends { zoom?: number }>(
  obj: T,
  zoom: number
): asserts obj is T & { zoom: number } {
  obj.zoom = zoom
}

/**
 * extract street/city/state/box etc info from a google autocomplete response
 *
 * @param args
 * @param args.box
 * @param args.citySlug
 * @param args.hoodSlug
 * @param args.height
 * @param args.width
 * @param args.geos
 * @param args.lat
 * @param args.lng
 * @param args.type
 */
const getLocationFromGeos = (args: {
  box?: AutocompleteBoxType | SaveSearchLocationBox | null
  citySlug?: string
  geos?: Geo
  height?: number | null
  hoodSlug?: string
  lat: number
  lng: number
  type?: AutocompleteResult['type']
  width?: number | null
}) => {
  const { box, citySlug, geos = {} as Partial<Geo>, height, hoodSlug, lat, lng, type, width } = args
  // NOTE: We use this center for cities and hoods to correct for issues with
  // chomped coordinates.
  const center = { lat, lng }
  const newLocation: {
    box?: AutocompleteBoxType | SaveSearchLocationBox | null
    country?: string
    cityName?: string
    hoodName?: string
    isCityGeoType?: true
    isHoodGeoType?: true
    lat?: number | null
    lng?: number | null
    shouldOmitBox?: true
    stateName?: string
    url?: string
    zoom?: number
  } = {}
  const useGeos = type === AUTOCOMPLETE_ADDRESS
  const isAutocompleteHood = type === AUTOCOMPLETE_NEIGHBORHOOD

  // NOTE: geos.hood may not be the hood we're looking for. Here we're grabbing the
  // hood that is closest to the center. Notice that the getBestHood function will return undefined
  // if the center is not actually within any of the nearbyHoods.
  const geosHood =
    geos.hood || geos.nearbyHoods
      ? getBestHood({
          center,
          hood: geos.hood,
          isAutocompleteHood,
          nearbyHoods: geos.nearbyHoods,
          url: hoodSlug,
          useGeos,
        })
      : undefined
  // NOTE: The first city in the geos.cities array is not necessarily the city we're looking for.
  // Here we take the city closest to the center.
  const geosCity = geos.cities?.length ? getMatchingEntity(geos.cities, citySlug) : undefined

  const closestCity = geos.cities?.length ? getClosestEntity(center, geos.cities) : undefined
  const closestHood =
    geos.hood || geos.nearbyHoods
      ? getBestHood({
          center,
          hood: geos.hood,
          isAutocompleteHood,
          nearbyHoods: geos.nearbyHoods,
          url: hoodSlug,
          useGeos: true,
        })
      : undefined

  const hoodUrl = geosHood?.cityStateName || closestHood?.cityStateName
  const cityUrl = geosCity?.url
  const closestCityUrl = closestCity?.url
  const hoodName = geosHood?.name || closestHood?.name
  const cityName = geosCity?.name
  const stateName = geosCity?.state

  let geoType:
    | typeof LocationC.NEIGHBORHOOD
    | typeof LocationC.LOCALITY
    | typeof LocationC.ADDRESS
    | undefined
  if (type === AUTOCOMPLETE_NEIGHBORHOOD || type === AUTOCOMPLETE_POSTCODE) {
    geoType = LocationC.NEIGHBORHOOD
  } else if (type === AUTOCOMPLETE_CITY) {
    geoType = LocationC.LOCALITY
  } else if (type === AUTOCOMPLETE_ADDRESS) {
    geoType = LocationC.ADDRESS
  } else if (hoodUrl) {
    geoType = LocationC.NEIGHBORHOOD
  } else if (cityUrl) {
    geoType = LocationC.LOCALITY
  }

  newLocation.cityName = cityName
  newLocation.stateName = stateName

  if (geoType === LocationC.NEIGHBORHOOD) {
    newLocation.hoodName = hoodName
    setZoom(newLocation, ZOOM_HOOD)
    newLocation.lat = geosHood?.lat || geosCity?.lat || closestHood?.lat || lat
    newLocation.lng = geosHood?.lng || geosCity?.lng || closestHood?.lng || lng
    newLocation.box =
      box ||
      (geosHood
        ? getBoxFromHoodOrCity(geosHood, geosCity)
        : getBoxFromHoodOrCity(closestHood, geosCity))
    if (hoodUrl) {
      newLocation.shouldOmitBox = true
      newLocation.isHoodGeoType = true
    }
    newLocation.url = hoodUrl || cityUrl
  } else if (geoType === LocationC.ADDRESS) {
    newLocation.url = hoodUrl || cityUrl || closestCityUrl
    newLocation.lat = lat
    newLocation.lng = lng
    setZoom(newLocation, ZOOM_ADDRESS)
    newLocation.box = getFittedBounds({ center, zoom: ZOOM_ADDRESS, dimensions: { height, width } })
  } else {
    setZoom(newLocation, ZOOM_CITY)
    // use cityUrl and fallback to hoodUrl if not available
    newLocation.url = cityUrl || hoodUrl || closestCityUrl
    newLocation.lat = geosCity?.lat || geosHood?.lat || lat
    newLocation.lng = geosCity?.lng || geosHood?.lng || lng
    newLocation.box = getBoxFromHoodOrCity(geosCity, geosHood) || box
    if (cityUrl) {
      newLocation.shouldOmitBox = true
      newLocation.isCityGeoType = true
    }
  }

  // for non-state page, set bounds if not set yet
  if (!newLocation.box) {
    if (
      // avoid creating a fakebox for state and country level
      lat &&
      lng &&
      !newLocation.url &&
      newLocation.zoom > ZOOM_STATE
    ) {
      let fallbackBoxDistance = DEFAULT_FALLBACK_BOX_DISTANCE
      if (newLocation.zoom >= ZOOM_STREET) {
        fallbackBoxDistance = STREET_FALLBACK_BOX_DISTANCE
      } else if (newLocation.zoom >= ZOOM_HOOD) {
        fallbackBoxDistance = HOOD_FALLBACK_BOX_DISTANCE
      }
      // Creating a fake bounding box
      newLocation.box = getQueryBounds(
        calculateBoxForListing({
          miles: fallbackBoxDistance,
          lat,
          lng,
        })
      )
    }
  }
  const isStateZoom = newLocation.zoom < ZOOM_CITY
  // fit the box to the map dimensions (Allow states to keep the box google provided)
  if (!isStateZoom && isValidBounds(newLocation.box)) {
    // TODO: it's maybe better to use the center of the city/hood if it's available. If the box we
    // have here came from the city/hood, the city/hood center is going to be better than the box
    // center.

    const offset = newLocation.shouldOmitBox ? 0.5 : 0
    const newZoom =
      getFittedZoom({
        bounds: newLocation.box,
        dimensions: { height, width },
        tileSize: TILE_SIZE,
        allowFloat: true,
      }) - offset

    if (isNumber(newZoom)) {
      newLocation.zoom = Math.max(newZoom, ZOOM_CITY_MIN)
    }
  }
  return newLocation
}

export default getLocationFromGeos
